import { appIntl } from '@components/locale/IntlGlobalProvider';

import { ExternalReplayStream, Stream, VideoProtocolEnum } from '@/types/commons/commons.types';
import {
  CAMERA_SENSORS,
  CameraConfiguration,
  CameraType,
  SensorOrSubSensorConfiguration,
} from '@/types/sensor/configuration.types';
import {
  ActiveMonitoringSensor,
  ActiveMonitoringSensorByType,
  CameraActiveMonitoringSensor,
  MonitoringSensor,
  MonitoringSensorByType,
} from '@/types/sensor/monitoring.types';
import {
  AutomatonSegmentStatus,
  HealthStatusEnum,
  HolopticsSensorStatus,
  SensorTypeEnum,
  SubCameraConfiguration,
  TrackingStatusEnum,
  VideoStatus,
} from '@/types/sensor/sensors';
import { CameraStatus, SensorOrSubSensorStatus, SensorStatus } from '@/types/sensor/status.types';

import {
  isMonitoringSensorOfType,
  isSensorOrSubSensorConfigurationOfType,
  isSensorOrSubSensorStatusOfType,
} from '../sensors.utils';

export function isCameraConfigurationType(
  sensorConfiguration: SensorOrSubSensorConfiguration | undefined | null,
): sensorConfiguration is CameraConfiguration {
  return CAMERA_SENSORS.some((type) => isSensorOrSubSensorConfigurationOfType(sensorConfiguration, type));
}

export function isCameraStatusType(
  sensorStatus: SensorOrSubSensorStatus | AutomatonSegmentStatus | null | undefined,
): sensorStatus is CameraStatus {
  return CAMERA_SENSORS.some((type) => isSensorOrSubSensorStatusOfType(sensorStatus, type));
}

export function isCameraActiveMonitoringSensor(
  monitoringSensor: ActiveMonitoringSensor,
): monitoringSensor is CameraActiveMonitoringSensor {
  return CAMERA_SENSORS.some((type) => isMonitoringSensorOfType(monitoringSensor, type));
}

export function isCameraMonitoringSensor(
  monitoringSensor: MonitoringSensor,
): monitoringSensor is MonitoringSensorByType<CameraType> {
  return CAMERA_SENSORS.some((type) => isMonitoringSensorOfType(monitoringSensor, type));
}

export function compareCameraRange(s1: VideoStatus, s2: VideoStatus): number {
  if (!s1.fov && !s2.fov) {
    return 0;
  }
  if (!s1.fov) {
    return 1;
  }
  if (!s2.fov) {
    return -1;
  }
  return s2.fov - s1.fov;
}

export function isHealthy(sensorStatus: SensorStatus): boolean {
  return sensorStatus.effectiveStatus === HealthStatusEnum.HEALTHY;
}

function isMovableHoloptics(sensorStatus: HolopticsSensorStatus): boolean {
  return (
    isHealthy(sensorStatus) ||
    (sensorStatus.effectiveStatus === HealthStatusEnum.DEGRADED && sensorStatus.systemStatus.clientConnected)
  );
}

export function isMovableCamera(monitoringSensor: ActiveMonitoringSensor): boolean {
  switch (monitoringSensor.status.type) {
    case SensorTypeEnum.AF2206:
      return isMovableHoloptics(monitoringSensor.status);
    case SensorTypeEnum.AXIS:
    case SensorTypeEnum.ONVIF:
    case SensorTypeEnum.ZMER:
      return isHealthy(monitoringSensor.status);
    default:
      return false;
  }
}

//Return the default flux used in cameraControl when a new configuration is selected
export function getDefaultFlux(cameraConfiguration: CameraConfiguration | null): SubCameraConfiguration | null {
  return cameraConfiguration?.subCameras.toSorted((camera1, camera2) => camera1.cameraId - camera2.cameraId)[0] ?? null;
}

export function calculateVerticalFov(fov?: number | null, resolutionX?: number, resolutionY?: number, precision = 1) {
  if (!fov || !resolutionX || !resolutionY) {
    return 0;
  }
  return Number((fov / (resolutionX / resolutionY)).toFixed(precision));
}

export function getNimbleStreamFromUrl(url: string | null) {
  if (!url) {
    return '';
  }
  if (url.startsWith('ws')) {
    return url;
  }

  return (
    (window['location'].protocol === 'https:' ? 'wss://' : 'ws://') +
    (import.meta.env.VITE_NIMBLE_URL?.split('://')[1] ?? window['location'].hostname) +
    url
  );
}

export function computePlayerStreamUrl(stream: Stream, replayMode = false) {
  const url = stream.url ?? stream.sourceUrl;
  const liveUrl = stream.protocol === VideoProtocolEnum.HLS ? (url ?? '') : getNimbleStreamFromUrl(url);
  return replayMode ? liveUrl.replace('ws', 'http') : liveUrl;
}

export function getNimbleExternalReplayUrl(stream: ExternalReplayStream) {
  const url = stream.replayUrl ?? stream.sourceReplayUrl;
  return url && getNimbleStreamFromUrl(url);
}

export function getContextMenuCameraLabel({ status, configuration }: ActiveMonitoringSensorByType<CameraType>) {
  const formatMessage = appIntl().formatMessage;
  if (status.trackingStatus.globalTrackingState === TrackingStatusEnum.ACTIVE) {
    return formatMessage({ id: 'contextmenu.label.inTracking' }, { name: configuration.name });
  }
  if (status.roundOn) {
    return formatMessage({ id: 'contextmenu.label.inRound' }, { name: configuration.name });
  }
  return configuration.name;
}

export function getTimeBeforeTurnOff(effectiveTime: string | null, situationTime: string | null): number | null {
  if (!effectiveTime || !situationTime) {
    return null;
  }

  const situationDate = new Date(situationTime);
  if (isNaN(situationDate.getTime())) {
    return null;
  }

  const tzMatch = situationTime.match(/([+-])(\d{2}):(\d{2})$/);
  const tzOffsetMinutes = tzMatch
    ? (tzMatch[1] === '-' ? -1 : 1) * (parseInt(tzMatch[2]) * 60 + parseInt(tzMatch[3]))
    : 0;

  const year = situationDate.getUTCFullYear();
  const month = situationDate.getUTCMonth();
  const day = situationDate.getUTCDate();

  const [hour, minute, second] = effectiveTime.split(':').map(Number);

  const effectiveUTC = Date.UTC(year, month, day, hour, minute, second) - tzOffsetMinutes * 60 * 1000;

  return effectiveUTC - situationDate.getTime();
}
